import { parseChildStepId } from './fanout.ts';
import { scopeCovers } from './resource-url.ts';
import type { ModelRef } from './model.ts';
import type { PolicySpec } from './policy.ts';
import {
  type ConnectionRef,
  type RuntimeKind,
  type SkillRef,
  type StepConfig,
  type StepLimits,
  type WatchSpec,
  type WorkflowConfig,
  stepAncestry,
} from './workflow.ts';
import { mergeWorkspace, type ResolvedWorkspace } from './workspace.ts';

/**
 * A step's configuration after inheritance is applied. This is what the runtime
 * actually enforces — nothing reads a raw `StepConfig` at execution time.
 *
 * Two inheritance directions, deliberately different:
 *
 * - **Capabilities are additive.** Connections, network hosts, and skills union
 *   down the tree. A child inherits everything its ancestors mount and may add
 *   more; it can never drop a parent's connection. That is the rule the product
 *   spec calls out, and it keeps a nested step from quietly escaping the
 *   surface its parent was reviewed with.
 * - **Restrictions are monotonically tightening.** Policy, tool allowlists, and
 *   limits narrow as you descend. A child may be stricter than its parent; it
 *   can never be laxer. This is what stops "add a level-3 step" from being a
 *   privilege-escalation move.
 * - **`workspace` tightens, and that is not the obvious answer.** A repository
 *   looks like a capability, so it looks additive. It is not: the capability is
 *   the mounted `git` connection, and `repos` narrows which repositories out of
 *   everything that mount could reach this step's disk is prepared with — the
 *   same kind of field as `ConnectionRef.tools`, which intersects. Union would
 *   make adding a level-3 step a way to stand in a repository the parent a
 *   reviewer actually reads never named. `mergeWorkspace` argues it in full.
 *
 * Because capabilities widen and restrictions tighten, the safe posture is to
 * express intent as policy rather than as connection surface.
 */
export interface ResolvedStep {
  workflowId: string;
  stepId: string;
  name: string;
  level: number;
  parentId: string | null;
  prompt: string;
  runtime: RuntimeKind;
  networkAllowlist: string[];
  /** `null` means "no explicit allowlist" — every tool the connections expose. */
  toolsAllowed: string[] | null;
  /** Either an inline spec or an id to load from `/policies`. Resolution happens in `@reef/policy`. */
  policyChain: Array<PolicySpec | string>;
  connections: ConnectionRef[];
  skills: SkillRef[];
  limits: Required<StepLimits>;
  requiresApproval: boolean;
  /**
   * The watch spec, when this step is a watcher.
   *
   * Not inherited: a watcher is a property of one step, and a workflow-level
   * default would turn every step under it into one.
   */
  watch: WatchSpec | null;
  approvalChannel: string | null;
  /**
   * The step's own choice, unresolved: a `provider/model` string or the triple.
   * Widened from `string` when `StepConfig.model` was, and deliberately left
   * *unresolved* here — resolution needs the acting person's preference and the
   * deployment default, neither of which a pure inheritance walk can see.
   */
  model: ModelRef | null;
  /**
   * The repositories this step's disk is prepared with, after tightening.
   *
   * `null` when nothing in the chain declared one, which is the common case and
   * is not the same as an empty set: nothing declared means the old behaviour —
   * whatever a warm lease seeds off a codebase-index mount — while a declared
   * empty set with an environment means "the environment's repositories".
   */
  workspace: ResolvedWorkspace | null;
  ancestry: string[];
}

const DEFAULT_LIMITS: Required<StepLimits> = {
  maxRuntimeMs: 60 * 60 * 1000,
  maxSpendCents: 10_000,
};

function mergeConnections(inherited: ConnectionRef[], own: readonly ConnectionRef[]): ConnectionRef[] {
  const byMount = new Map<string, ConnectionRef>();
  for (const connection of inherited) byMount.set(connection.as, connection);

  for (const connection of own) {
    const parent = byMount.get(connection.as);
    if (!parent) {
      byMount.set(connection.as, { ...connection });
      continue;
    }
    if (parent.id !== connection.id) {
      // Re-binding an inherited mount name to a different connection would let a
      // child swap `db` from staging to prod under the parent's instructions.
      throw new Error(
        `step cannot rebind inherited connection mount "${connection.as}" from "${parent.id}" to "${connection.id}"`,
      );
    }
    // Same reasoning one level down, but only where the ancestor actually made
    // a promise. A parent that pinned Linear to the acting human, with
    // instructions written in that voice, must not have a child silently
    // re-point the same mount at the workspace bot — or the reverse.
    //
    // An ancestor that left the actor unset (or explicitly `auto`) promised
    // nothing about identity, so a descendant choosing one is specifying, not
    // contradicting. That is the common shape: the workflow mounts the
    // connection once, and each step says how it intends to speak.
    const parentPinned = parent.actor && parent.actor !== 'auto' ? parent.actor : null;
    const childPinned = connection.actor && connection.actor !== 'auto' ? connection.actor : null;

    if (parentPinned && childPinned && parentPinned !== childPinned) {
      throw new Error(
        `step cannot change the actor of inherited connection mount "${connection.as}" from "${parentPinned}" to "${childPinned}"`,
      );
    }

    byMount.set(connection.as, {
      ...parent,
      actor: parentPinned ?? childPinned ?? parent.actor ?? connection.actor,
      // Tool lists intersect: a child narrows what it will use, never widens.
      tools: intersectOptional(parent.tools, connection.tools),
      scope: narrowScope(connection.as, parent.scope, connection.scope),
    });
  }

  return [...byMount.values()];
}

/**
 * A child's data scope under its parent's, for one mount.
 *
 * `tools` can intersect by set membership because a tool name is a literal.
 * Scopes are patterns, so "narrower" is a containment question and set
 * intersection would answer it wrongly in the direction that matters: a parent
 * scoped to one database and a child scoped to the whole service share no
 * string, so an intersection is empty — which reads as "no restriction" in the
 * shape every other optional field uses, and hands the child more than the
 * parent had.
 *
 * So this refuses instead. A child that names data outside its parent's scope
 * is a mistake with two readings and no safe default, and the author is looking
 * at the document.
 */
function narrowScope(
  mount: string,
  parent: readonly string[] | undefined,
  child: readonly string[] | undefined,
): string[] | undefined {
  if (!child?.length) return parent ? [...parent] : undefined;
  if (!parent?.length) return [...child];

  const escaped = child.filter((entry) => !parent.some((wide) => scopeCovers(wide, entry)));
  if (escaped.length > 0) {
    throw new Error(
      `step cannot widen the scope of inherited connection mount "${mount}": ` +
        `${escaped.join(', ')} ${escaped.length === 1 ? 'is' : 'are'} outside ${parent.join(', ')}`,
    );
  }
  return [...child];
}

function intersectOptional(
  parent: readonly string[] | undefined,
  child: readonly string[] | undefined,
): string[] | undefined {
  if (!parent && !child) return undefined;
  if (!parent) return [...child!];
  if (!child) return [...parent];
  const parentSet = new Set(parent);
  return child.filter((entry) => parentSet.has(entry));
}

function unionStrings(...lists: Array<readonly string[] | undefined>): string[] {
  const out = new Set<string>();
  for (const list of lists) for (const entry of list ?? []) out.add(entry);
  return [...out];
}

/**
 * Collapse a step's ancestry into the configuration the runtime enforces.
 *
 * Throws when the step id is not in the workflow, or when a child tries to
 * rebind an inherited connection mount.
 */
export function resolveStep(workflow: WorkflowConfig, stepId: string): ResolvedStep {
  /*
   * A fan-out child is not in the document, and cannot be: it exists because a
   * run discovered a list the author could not have known the length of. What
   * it *is* is its parent applied to one item — same prompt, same policy chain,
   * same connections, same ceilings — so it resolves against the parent and
   * keeps its own id.
   *
   * Only the id differs, and that difference is the one that has to survive:
   * the conversation, the step row, the audit trail and the fan-out row all key
   * on `parent.3`, and collapsing it to `parent` here would merge forty
   * children into one conversation.
   */
  const child = parseChildStepId(stepId);
  const chain = stepAncestry(workflow, stepId) ?? (child ? stepAncestry(workflow, child.parentStepId) : null);
  if (!chain) throw new Error(`step "${stepId}" is not part of workflow "${workflow.id}"`);

  let runtime: RuntimeKind = workflow.runtime ?? 'isolate';
  let network = [...(workflow.networkAllowlist ?? [])];
  let connections = mergeConnections([], workflow.mcpConnections ?? []);
  let skills: SkillRef[] = [...(workflow.skills ?? [])];
  let toolsAllowed: string[] | null = null;
  const policyChain: Array<PolicySpec | string> = workflow.policy ? [workflow.policy] : [];
  let limits: Required<StepLimits> = {
    maxRuntimeMs: workflow.limits?.maxRuntimeMs ?? DEFAULT_LIMITS.maxRuntimeMs,
    maxSpendCents: workflow.limits?.maxSpendCents ?? DEFAULT_LIMITS.maxSpendCents,
  };

  let workspace: ResolvedWorkspace | null = mergeWorkspace(null, workflow.workspace);

  let leaf: StepConfig = chain[chain.length - 1]!;

  for (const step of chain) {
    leaf = step;
    if (step.runtime) runtime = step.runtime;
    network = unionStrings(network, step.networkAllowlist);
    connections = mergeConnections(connections, step.mcpConnections ?? []);
    skills = dedupeSkills([...skills, ...(step.skills ?? [])]);
    toolsAllowed = intersectOptional(toolsAllowed ?? undefined, step.toolsAllowed) ?? null;
    if (step.policy) policyChain.push(step.policy);
    workspace = mergeWorkspace(workspace, step.workspace);
    limits = {
      maxRuntimeMs: Math.min(limits.maxRuntimeMs, step.limits?.maxRuntimeMs ?? Infinity),
      maxSpendCents: Math.min(limits.maxSpendCents, step.limits?.maxSpendCents ?? Infinity),
    };
  }

  return {
    workflowId: workflow.id,
    stepId,
    name: leaf.name,
    level: leaf.level,
    parentId: leaf.parentId ?? null,
    prompt: leaf.prompt,
    runtime,
    networkAllowlist: network,
    toolsAllowed,
    policyChain,
    connections,
    skills,
    limits,
    requiresApproval: leaf.requiresApproval ?? false,
    // A fan-out child resolves against its parent, so a watcher's child does
    // not inherit the watch — `leaf` here is the step itself.
    watch: leaf.watch ?? null,
    approvalChannel: leaf.approvalChannel ?? null,
    model: leaf.model ?? null,
    workspace,
    ancestry: chain.map((step) => step.id),
  };
}

function dedupeSkills(skills: readonly SkillRef[]): SkillRef[] {
  const seen = new Set<string>();
  const out: SkillRef[] = [];
  for (const skill of skills) {
    if (seen.has(skill.name)) continue;
    seen.add(skill.name);
    out.push(skill);
  }
  return out;
}
